/**
 * Benutzer verwalten.
 *
 * Der einzige Bereich, der Verbindung voraussetzt — und der einzige, bei dem
 * das richtig ist. Zugaenge zu vergeben ist Schreibtischarbeit; offline eine
 * Rolle zu aendern und die Aenderung dann abzugleichen waere eine Einladung
 * zum Durcheinander.
 *
 * Was die Oberflaeche hier zeigt, entscheidet nicht, was erlaubt ist. Die
 * Rollenpruefung sitzt im Server.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { anmeldung } from '../daten/abgleich.ts';
import { Passwortfeld } from './Passwortfeld.tsx';

type Rolle = 'vertrieb' | 'monteur' | 'buero' | 'admin';

const ROLLENNAMEN: Record<Rolle, string> = {
  vertrieb: 'Vertrieb',
  monteur: 'Monteur',
  buero: 'Büro',
  admin: 'Verwaltung',
};

interface Benutzerzeile {
  id: string;
  name: string;
  email: string;
  rolle: Rolle;
  aktiv: boolean;
  angelegt_am: string;
  letzte_anmeldung: string | null;
  offene_sitzungen: number;
  geloescht_am: string | null;
}

async function ruf(pfad: string, optionen: RequestInit = {}): Promise<Response> {
  const an = await anmeldung();
  return fetch(pfad, {
    ...optionen,
    headers: {
      'Content-Type': 'application/json',
      ...(an ? { Authorization: `Bearer ${an.token}` } : {}),
      ...(optionen.headers ?? {}),
    },
  });
}

export function Benutzerverwaltung({
  rolle,
  online,
}: {
  rolle: string;
  online: boolean;
}): React.JSX.Element {
  const [benutzer, setBenutzer] = useState<Benutzerzeile[] | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState({ name: '', email: '', passwort: '', rolle: 'vertrieb' as Rolle });
  const [passwortFuer, setPasswortFuer] = useState<string | null>(null);
  const [neuesPasswort, setNeuesPasswort] = useState('');

  const darfVerwalten = rolle === 'admin';
  const meldungsfeld = useRef<HTMLDivElement>(null);
  const vorhandene = (benutzer ?? []).filter((b) => !b.geloescht_am);
  const entfernte = (benutzer ?? []).filter((b) => b.geloescht_am);

  // Was noch fehlt, damit "Anlegen" geht — im Klartext, nicht als grauer Knopf.
  const fehlendesZumAnlegen: string | null = !neu.name
    ? 'Es fehlt der Name.'
    : !neu.email
      ? 'Es fehlt die E-Mail-Adresse.'
      : neu.passwort.length < 10
        ? `Das Passwort braucht zehn Zeichen, bisher sind es ${neu.passwort.length}.`
        : null;

  // Die Meldung stand bisher ganz oben im Bereich, waehrend der Knopf dazu —
  // "Anlegen" — ganz unten sitzt. Bei einem Fehler passierte auf dem Bildschirm
  // sichtbar nichts. Deshalb wird sie jetzt in den Blick geholt.
  useEffect(() => {
    if (meldung || fehler) meldungsfeld.current?.scrollIntoView({ block: 'nearest' });
  }, [meldung, fehler]);

  const laden = useCallback(async () => {
    if (!darfVerwalten || !online) return;
    setFehler(null);
    try {
      const antwort = await ruf('/api/benutzer?mitEntfernten=ja');
      if (!antwort.ok) {
        setFehler((await antwort.json()).fehler ?? `Fehler ${antwort.status}`);
        return;
      }
      setBenutzer((await antwort.json()).benutzer);
    } catch {
      setFehler('Der Server ist nicht erreichbar.');
    }
  }, [darfVerwalten, online]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function schicke(pfad: string, methode: string, koerper?: unknown): Promise<void> {
    setFehler(null);
    setMeldung(null);
    try {
      const antwort = await ruf(pfad, {
        method: methode,
        ...(koerper ? { body: JSON.stringify(koerper) } : {}),
      });
      const daten = await antwort.json().catch(() => ({}));
      if (!antwort.ok) {
        setFehler(daten.fehler ?? `Fehler ${antwort.status}`);
        return;
      }
      setMeldung(daten.meldung ?? 'Übernommen.');
      await laden();
    } catch {
      setFehler('Der Server ist nicht erreichbar.');
    }
  }

  return (
    <section>
      <h2>Benutzer</h2>

      {/* Das eigene Passwort darf jeder aendern — auch ohne Verwaltungsrolle. */}
      <EigenesPasswort online={online} />

      {!darfVerwalten ? (
        <p className="klein" style={{ marginTop: '1.5rem' }}>
          Andere Benutzer verwalten darf nur, wer die Rolle Verwaltung hat.
        </p>
      ) : !online ? (
        <div className="hinweis">
          Ohne Verbindung. Benutzer zu verwalten geht nur am Server — alles andere in diesem
          Programm läuft auch offline, das hier bewusst nicht.
        </div>
      ) : (
        <>
          <div ref={meldungsfeld}>
            {fehler && <div className="hinweis hinweis--fehler">{fehler}</div>}
            {meldung && <div className="hinweis hinweis--gut">{meldung}</div>}
          </div>

          <h3 style={{ marginTop: '1.5rem' }}>Angelegt ({vorhandene.length})</h3>

          {benutzer === null ? (
            <p className="gedaempft">Einen Moment …</p>
          ) : (
            vorhandene.map((b) => (
              <div key={b.id} className="kachel" style={{ opacity: b.aktiv ? 1 : 0.55 }}>
                <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'baseline' }}>
                  <span style={{ flex: 1 }}>
                    <strong style={{ fontWeight: 600 }}>{b.name}</strong>
                    {!b.aktiv && <span style={{ color: 'var(--waerme)' }}> · stillgelegt</span>}
                    <span style={{ display: 'block', color: 'var(--grau)', fontSize: '0.78rem' }}>
                      {b.email}
                      {b.letzte_anmeldung
                        ? ` · zuletzt ${new Date(b.letzte_anmeldung).toLocaleDateString('de-AT')}`
                        : ' · noch nie angemeldet'}
                      {b.offene_sitzungen > 0 ? ` · ${b.offene_sitzungen} offene Sitzung(en)` : ''}
                    </span>
                  </span>
                </div>

                <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
                  <select
                    value={b.rolle}
                    onChange={(e) =>
                      void schicke(`/api/benutzer/${b.id}`, 'PATCH', { rolle: e.target.value })
                    }
                    style={{ width: 'auto' }}
                  >
                    {(Object.keys(ROLLENNAMEN) as Rolle[]).map((r) => (
                      <option key={r} value={r}>
                        {ROLLENNAMEN[r]}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() =>
                      void schicke(`/api/benutzer/${b.id}`, 'PATCH', { aktiv: !b.aktiv })
                    }
                  >
                    {b.aktiv ? 'Stilllegen' : 'Wieder freigeben'}
                  </button>

                  <button onClick={() => setPasswortFuer(passwortFuer === b.id ? null : b.id)}>
                    Passwort setzen
                  </button>

                  <button
                    className="gefaehrlich"
                    onClick={() => {
                      if (
                        confirm(
                          `${b.name} (${b.email}) entfernen?\n\nDas Konto verschwindet aus der Liste und die Anmeldung geht nicht mehr. Rückgängig machen lässt es sich unter "Entfernt".`,
                        )
                      ) {
                        void schicke(`/api/benutzer/${b.id}`, 'DELETE');
                      }
                    }}
                  >
                    Entfernen
                  </button>
                </div>

                {passwortFuer === b.id && (
                  <div className="knopfreihe" style={{ marginTop: '0.5rem' }}>
                    <Passwortfeld
                      placeholder="Neues Passwort, mindestens zehn Zeichen"
                      value={neuesPasswort}
                      onChange={(e) => setNeuesPasswort(e.target.value)}
                      style={{ width: '18rem' }}
                    />
                    <button
                      className="wichtig"
                      disabled={neuesPasswort.length < 10}
                      onClick={() => {
                        void schicke(`/api/benutzer/${b.id}/passwort`, 'POST', {
                          passwort: neuesPasswort,
                        }).then(() => {
                          setNeuesPasswort('');
                          setPasswortFuer(null);
                        });
                      }}
                    >
                      Setzen
                    </button>
                  </div>
                )}
              </div>
            ))
          )}

          {/* --- Entfernte ----------------------------------------------- */}
          {entfernte.length > 0 && (
            <>
              <h3 style={{ marginTop: '1.75rem' }}>Entfernt ({entfernte.length})</h3>
              <p className="klein" style={{ margin: '0 0 0.6rem' }}>
                Entfernte Konten bleiben stehen, damit sichtbar bleibt, wer welches Projekt angelegt
                hat. Wer zurückkommt, meldet sich mit demselben Passwort an wie vorher.
              </p>
              {entfernte.map((b) => (
                <div key={b.id} className="kachel" style={{ opacity: 0.7 }}>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'baseline' }}>
                    <span style={{ flex: 1 }}>
                      <strong style={{ fontWeight: 600 }}>{b.name}</strong>
                      <span className="klein" style={{ display: 'block' }}>
                        {b.email} · entfernt am {new Date(b.geloescht_am!).toLocaleString('de-AT')}
                      </span>
                    </span>
                    <button onClick={() => void schicke(`/api/benutzer/${b.id}/aufnehmen`, 'POST')}>
                      Wieder aufnehmen
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* --- Anlegen ------------------------------------------------- */}
          <h3 style={{ marginTop: '1.75rem' }}>Benutzer anlegen</h3>
          <div className="feld">
            <input
              placeholder="Name"
              value={neu.name}
              onChange={(e) => setNeu({ ...neu, name: e.target.value })}
            />
          </div>
          <div className="feld">
            <input
              type="email"
              placeholder="E-Mail"
              value={neu.email}
              onChange={(e) => setNeu({ ...neu, email: e.target.value })}
            />
          </div>
          <div className="knopfreihe">
            <Passwortfeld
              placeholder="Passwort, mindestens zehn Zeichen"
              value={neu.passwort}
              onChange={(e) => setNeu({ ...neu, passwort: e.target.value })}
              style={{ width: '16rem' }}
            />
            <select
              value={neu.rolle}
              onChange={(e) => setNeu({ ...neu, rolle: e.target.value as Rolle })}
              style={{ width: 'auto' }}
            >
              {(Object.keys(ROLLENNAMEN) as Rolle[]).map((r) => (
                <option key={r} value={r}>
                  {ROLLENNAMEN[r]}
                </option>
              ))}
            </select>
            <button
              className="wichtig"
              disabled={fehlendesZumAnlegen !== null}
              title={fehlendesZumAnlegen ?? 'Benutzer anlegen'}
              onClick={() =>
                void schicke('/api/benutzer', 'POST', neu).then(() =>
                  setNeu({ name: '', email: '', passwort: '', rolle: 'vertrieb' }),
                )
              }
            >
              Anlegen
            </button>
          </div>

          {/* Ein grauer Knopf ohne Begruendung ist eine Sackgasse. */}
          {fehlendesZumAnlegen && <p className="klein">{fehlendesZumAnlegen}</p>}

          <p style={{ fontSize: '0.75rem', color: 'var(--grau)', marginTop: '1.25rem' }}>
            Wer stillgelegt oder zurückgestuft wird, verliert seine offenen Sitzungen sofort. Der
            letzte Benutzer mit der Rolle Verwaltung lässt sich nicht entfernen — sonst käme niemand
            mehr hier herein.
          </p>
        </>
      )}
    </section>
  );
}

/** Das eigene Passwort — nur mit dem bisherigen. */
function EigenesPasswort({ online }: { online: boolean }): React.JSX.Element {
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [stand, setStand] = useState<string | null>(null);

  async function aendere(): Promise<void> {
    setStand(null);
    const antwort = await ruf('/api/ich/passwort', {
      method: 'POST',
      body: JSON.stringify({ altesPasswort: alt, neuesPasswort: neu }),
    });
    const daten = await antwort.json().catch(() => ({}));
    if (antwort.ok) {
      setStand('Passwort geändert.');
      setAlt('');
      setNeu('');
    } else {
      setStand(daten.fehler ?? `Fehler ${antwort.status}`);
    }
  }

  return (
    <div className="kachel">
      <h3 style={{ marginTop: 0 }}>Eigenes Passwort</h3>
      <div className="knopfreihe">
        <Passwortfeld
          placeholder="Bisheriges"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          style={{ width: '12rem' }}
        />
        <Passwortfeld
          placeholder="Neues, mindestens zehn Zeichen"
          value={neu}
          onChange={(e) => setNeu(e.target.value)}
          style={{ width: '16rem' }}
        />
        <button disabled={!online || !alt || neu.length < 10} onClick={() => void aendere()}>
          Ändern
        </button>
      </div>
      {!online && <p className="klein">Dafür braucht es Verbindung.</p>}
      {stand && <div className="hinweis">{stand}</div>}
    </div>
  );
}
